import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Pause, Play } from "lucide-react";
import { MaskLines, FadeUp } from "../components/Reveal";
import SubscribeForm from "../components/SubscribeForm";
import SaveButton from "../components/SaveButton";
import AskWork from "../components/AskWork";
import Reflections from "../components/Reflections";
import NarrateButton from "../components/NarrateButton";
import LiveContext from "../components/LiveContext";
import { useAuth } from "../context/AuthContext";
import { useAudio } from "../context/AudioContext";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

function Body({ paras }) {
  return paras.map((t, i) =>
    t.startsWith("## ") ? (
      <h2 key={i} className="f-display font-black uppercase text-3xl sm:text-4xl mt-16 mb-6 leading-none">{t.slice(3)}</h2>
    ) : (
      <p key={i} className={`text-lg sm:text-xl leading-[1.75] text-[#d8d7d1] mb-7 whitespace-pre-line ${i === 0 && /^[A-Za-z]/.test(t) ? "dropcap" : ""}`}>{t}</p>
    )
  );
}

export default function Piece() {
  const { slug } = useParams();
  const [piece, setPiece] = useState(null);
  const [missing, setMissing] = useState(false);
  const { tracks, current, playing, toggle } = useAudio();
  const { user, login } = useAuth();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setPiece(null);
    setMissing(false);
    api.get(`/pieces/${slug}`).then((r) => setPiece(r?.data ?? null)).catch(() => setMissing(true));
  }, [slug, user]);

  useSeo(piece?.title || "Explore", piece?.dek);

  const track = tracks.find((t) => t.slug === piece?.track_slug);
  const more = tracks.filter((t) => t.piece_slug === piece?.slug && t.slug !== track?.slug);
  const on = track && current?.slug === track.slug && playing;

  if (missing) {
    return (
      <div className="px-5 sm:px-8 lg:px-12 pt-40 pb-32" data-testid="piece-missing">
        <p className="f-serif italic text-3xl">This coordinate is empty.</p>
        <Link to="/explore" className="f-mono text-xs uppercase tracking-[0.2em] link-u mt-6 inline-block">Back to the index</Link>
      </div>
    );
  }
  if (!piece) return <div className="min-h-screen" />;

  return (
    <article className="pt-28 pb-24" data-testid="piece-page">
      <motion.div style={{ scaleX: progress }} className="fixed top-0 left-0 right-0 h-[2px] bg-[var(--signal)] origin-left z-[55]" />
      <header className="px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Link to="/explore" className="lg:col-span-12 f-mono text-xs uppercase tracking-[0.2em] text-[var(--ash)] hover:text-[var(--bone)] flex items-center gap-2 w-fit" data-testid="piece-back-link">
          <ArrowLeft size={14} /> Index
        </Link>
        <div className="lg:col-span-9">
          <p className="eyebrow mb-5">{piece.kind} &middot; No. {String(piece.order).padStart(2, "0")}</p>
          <h1 className="f-display font-black uppercase text-[16vw] sm:text-[11vw] lg:text-[8vw] leading-[0.84]" data-testid="piece-title">
            <MaskLines onMount lines={piece.title.split(" ").reduce((acc, w) => {
              const last = acc[acc.length - 1];
              if (last && (last + " " + w).length <= 14) acc[acc.length - 1] = last + " " + w; else acc.push(w);
              return acc;
            }, [])} />
          </h1>
          <p className="f-serif italic text-2xl sm:text-3xl text-[#cfcec8] mt-8 max-w-2xl leading-snug">{piece.dek}</p>
        </div>
      </header>

      {piece.video && (
        <FadeUp className="px-5 sm:px-8 lg:px-12 mt-16">
          <div className="relative aspect-video border border-[var(--line)] bg-[var(--ink-2)]" data-testid="piece-video">
            <iframe src={`https://player.vimeo.com/video/${piece.video}?dnt=1&color=ecebe6&title=0&byline=0&portrait=0`} title={`${piece.title} video`} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen loading="lazy" />
          </div>
          <p className="f-mono text-[11px] text-[var(--ash)] mt-3">The spoken film &middot; Vimeo</p>
        </FadeUp>
      )}

      {piece.image && (
        <FadeUp className="px-5 sm:px-8 lg:px-12 mt-16">
          <div className="relative aspect-[21/9] overflow-hidden" style={{ clipPath: "polygon(0 0, 92% 0, 100% 18%, 100% 100%, 0 100%)" }}>
            <img src={piece.image} alt={piece.title} className="w-full h-full object-cover grayscale contrast-125" />
          </div>
        </FadeUp>
      )}

      <div className="px-5 sm:px-8 lg:px-12 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-28 space-y-6">
            <SaveButton kind="piece" slug={piece.slug} />
            {piece.body?.length > 0 && <NarrateButton piece={piece} />}
            {piece.body?.length > 0 && <Reflections slug={piece.slug} />}
            {track && (
              <button onClick={() => toggle(track)} className="group w-full flex items-center gap-4 border border-[var(--line)] p-4 hover:border-[var(--bone)] transition-colors text-left" data-testid="piece-play-track">
                <span className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center border ${on ? "bg-[var(--signal)] border-[var(--signal)]" : "border-[var(--bone)]"}`}>
                  {on ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                </span>
                <span>
                  <span className="block f-mono text-[11px] text-[var(--ash)] uppercase">Listen alongside</span>
                  <span className="block f-display font-bold uppercase text-lg leading-tight">{track.version}</span>
                </span>
              </button>
            )}
            {more.length > 0 && (
              <ul className="border-t border-[var(--line)] max-h-80 overflow-y-auto" data-lenis-prevent data-testid="piece-more-audio">
                <li className="f-mono text-[11px] uppercase text-[var(--ash)] py-3">{more.length} more recording{more.length > 1 ? "s" : ""}</li>
                {more.map((m) => (
                  <li key={m.slug}>
                    <button onClick={() => toggle(m)} className={`w-full text-left py-2 flex items-center gap-3 hover:text-[var(--signal)] transition-colors ${current?.slug === m.slug ? "text-[var(--signal)]" : "text-[#cfcec8]"}`} data-testid={`piece-more-${m.slug}`}>
                      {current?.slug === m.slug && playing ? <Pause size={12} /> : <Play size={12} />}
                      <span className="text-sm leading-snug">{m.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <a href={piece.source_url} target="_blank" rel="noopener noreferrer" className="f-mono text-xs uppercase tracking-[0.15em] link-u flex items-center gap-2 w-fit text-[#cfcec8]" data-testid="piece-source-link">
              Original on outlawcreative.ink <ArrowUpRight size={13} />
            </a>
          </div>
        </aside>
        <div className="lg:col-span-7 order-1 lg:order-2 max-w-[68ch]" data-testid="piece-body">
          {piece.locked ? (
            <div className="border-l border-[var(--signal)] pl-6 py-2" data-testid="piece-members-lock">
              <p className="f-serif italic text-2xl leading-snug">Members only.</p>
              <p className="text-lg text-[#cfcec8] mt-4 leading-relaxed">This piece opens for readers who sign in.</p>
              <button onClick={login} className="mt-6 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-6 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors" data-testid="piece-members-login">Sign in with Google</button>
            </div>
          ) : piece.body?.length ? (
            <Body paras={piece.body} />
          ) : (
            <div className="border-l border-[var(--signal)] pl-6 py-2">
              <p className="f-serif italic text-2xl leading-snug">[Full text to be imported]</p>
              <p className="text-lg text-[#cfcec8] mt-4 leading-relaxed">This piece lives in the archive for now. Read it in full on the original site while it is being brought into this edition.</p>
            </div>
          )}
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-start-4 lg:col-span-7 space-y-10">
          <AskWork pieceSlug={piece.slug} title={piece.title} id="piece-ask" />
          {!piece.locked && <LiveContext slug={piece.slug} />}
        </div>
      </div>

      <section className="px-5 sm:px-8 lg:px-12 mt-28 grid grid-cols-1 lg:grid-cols-12 gap-8 border-t border-[var(--line)] pt-16">
        <p className="lg:col-span-5 f-serif italic text-3xl sm:text-4xl leading-tight">If it resonates, stay.</p>
        <div className="lg:col-span-7"><SubscribeForm id="piece-subscribe" /></div>
      </section>
    </article>
  );
}
